import parse from "html-react-parser"
import { useRef, useState } from "react"

import iconCheck from "images/icons/checkmark.svg?raw"
import { lockupLogoSvg } from "shared/utils/ifttt_logo"
import { planNameBadge } from "shared/utils/tiers"

import type { UIEvent, UIResponse, UpgradeRequest, UpgradeRequiredData, UpgradeRequiredTier } from "../types"
import { CardActions, CardButton, CardSettledAction, CardShell } from "./card_shell"
import styles from "./upgrade_required_card.module.scss"

interface Props {
  ui: UIEvent
  response?: UIResponse
  onRespond: (uiId: string, actionId: string, value: unknown, displayText?: string) => void
  onUpgradeRequest?: (request: UpgradeRequest) => void
}

const TIERS: Record<
  UpgradeRequiredTier,
  { displayName: string; badge: string; checkoutTarget: string; features: string[] }
> = {
  pro: {
    displayName: "Pro",
    badge: planNameBadge("Pro"),
    checkoutTarget: "intermediate_pro",
    features: [
      "20 Applets",
      "Webhooks, Twitter, and more",
      "Faster Applet speeds",
      "Multi-action Applets",
      "Exclusive services",
      "Customer support",
    ],
  },
  pro_plus: {
    displayName: "Pro+",
    badge: planNameBadge("Pro+"),
    checkoutTarget: "pro",
    features: [
      "Unlimited Applets",
      "Queries and filters",
      "Fastest speeds and support",
      "AI services",
      "Connect multiple accounts",
      "Everything in Pro",
    ],
  },
}

function satisfiesTier(checkoutModalTarget: string, tier: UpgradeRequiredTier): boolean {
  if (tier === "pro_plus") return checkoutModalTarget === "pro"
  return checkoutModalTarget === "intermediate_pro" || checkoutModalTarget === "pro"
}

const UpgradeRequiredCard = ({ ui, onRespond, onUpgradeRequest, response }: Props) => {
  const [answered, setAnswered] = useState(false)
  const answeredRef = useRef(false)

  const data = ui.data as UpgradeRequiredData
  const tier = TIERS[data.tier] ?? TIERS.pro
  const upgradeLabel = ui.actions?.find(action => action.id === "upgrade")?.label ?? `Upgrade to ${tier.displayName}`

  const panel = (
    <div className={styles["panel"]}>
      <div className={styles["branding"]}>
        <span className={styles["logo"]}>{parse(lockupLogoSvg())}</span>
        <span className={styles["badge"]}>{parse(tier.badge)}</span>
      </div>
      <ul className={styles["features"]}>
        {tier.features.map(feature => (
          <li key={feature} className={styles["feature"]}>
            <span className={styles["check"]} aria-hidden="true">
              {parse(iconCheck)}
            </span>
            {feature}
          </li>
        ))}
      </ul>
    </div>
  )

  if (response) {
    return (
      <CardShell>
        {panel}
        <CardSettledAction
          label={response.displayText ?? `Upgraded to ${tier.displayName}`}
          confirmed={response.actionId === "upgrade"}
        />
      </CardShell>
    )
  }

  const answer = () => {
    answeredRef.current = true
    setAnswered(true)
  }

  const close = () => {
    if (answeredRef.current) return
    answer()
    onRespond(ui.id, "close", undefined)
  }

  const upgrade = () => {
    if (answeredRef.current) return
    if (!onUpgradeRequest) {
      window.open("/plans", "_blank", "noopener")
      return
    }
    onUpgradeRequest({
      step: "applet_features",
      stepTier: tier.checkoutTarget,
      onUpgrade: checkoutModalTarget => {
        if (answeredRef.current || !satisfiesTier(checkoutModalTarget, data.tier)) return
        answer()
        onRespond(ui.id, "upgrade", data.tier, `Upgraded to ${tier.displayName}`)
      },
    })
  }

  return (
    <CardShell prompt={data.title} onDismiss={close}>
      {panel}
      <CardActions>
        <CardButton variant="primary" disabled={answered} onClick={upgrade}>
          {upgradeLabel}
        </CardButton>
      </CardActions>
    </CardShell>
  )
}

export default UpgradeRequiredCard
